/**
 * snowflake-setup.ts — Pure frontend SQL generator for Snowflake setup scripts.
 *
 * Generates Snowflake SQL to create a service user with key pair authentication, appropriate role
 * grants, and a verification block. All identifier validation happens at this boundary — callers
 * pass raw strings.
 *
 * ## Why the grants look the way they do
 *
 * The admin pastes this script into a worksheet as is, so whatever it omits, their account omits.
 * Two omissions were fixed together:
 *
 * - **ACT2-735.** The script granted one schema, and only plain tables and views. A new schema (a
 *   new source namespace, a Profiles output, a dbt target) got nothing, and a dynamic, Iceberg or
 *   external table or a materialized view got nothing even inside the granted schema, because
 *   Snowflake evaluates future grants per object type. `INFORMATION_SCHEMA.TABLES` only returns
 *   objects the role has a privilege on, so the customer saw a missing table rather than a
 *   permission error, and could not tell our script was the cause. Grants are now database-wide by
 *   default and cover every object type Reverse ETL or the agent can meet.
 * - **ACT2-737.** The script granted nothing on `_RUDDERSTACK`, the schema Reverse ETL writes its
 *   own sync tables to, so an account passed every Lookout check and then failed at the first
 *   activation. That block is now unconditional — an account that can be explored is an account
 *   that can be activated.
 *
 * Two Snowflake rules shape the output and are easy to get wrong:
 *
 * - **Schema-level future grants beat database-level ones and silence them.** "When future grants
 *   are defined on the same object type for a database and a schema in the same database, the
 *   schema-level grants take precedence over the database level grants, and the database level
 *   grants are ignored." So one pre-existing schema-level future grant on TABLES — to any role, not
 *   just ours — voids our database-level grant for that schema. The script therefore also emits the
 *   schema-level form for the schema the admin named, and says why.
 * - **Some rotations drop future grants entirely.** `CREATE OR REPLACE … COPY GRANTS` (dbt's
 *   `copy_grants: true`) does not inherit future grants, and neither rename nor swap applies them.
 *   No grant syntax fixes that, so the script cannot; the re-runnable grant block
 *   (`generateSnowflakeGrantSQL`) is the recovery path.
 *
 * ## Creating the user and role without colliding (ACT2-1116)
 *
 * The script is plain statements: no scripting block, no ACCOUNTADMIN. It runs as USERADMIN (create),
 * SECURITYADMIN (grant) and SYSADMIN (the Reverse ETL schema), and needs no warehouse. `USE ROLE`
 * checks the admin's own grants, so switching between the three works for a user who holds them.
 * Each name is decided on its own (`generatedUser`, `generatedRole`):
 *
 * - **Generated** (the derived default, whose suffix hashes workspace, account and key fingerprint):
 *   `CREATE … IF NOT EXISTS`. A same-named object is taken to be ours, so a re-run or a partial run
 *   completes.
 * - **Typed** by the admin: a plain `CREATE`, so Snowflake's own "Object 'X' already exists." stops
 *   the script before it touches someone else's object.
 *
 * The user is created **before** the role in every mode. Snowflake accepts a `DEFAULT_ROLE` that does
 * not exist yet, and a taken user name then stops the script before any role is made. (Role-first
 * left an orphan role behind in exactly that case.)
 *
 * Role modes (`roleMode`): `new` creates the role and grants it access; `existing` creates the user,
 * grants it the admin's role, and grants that role only USAGE on the compute warehouse (and Lookout's
 * own, when set) and on the database. Lookout's Test runs a query, so without those it fails (000606
 * "No active warehouse selected", 090105 "no current database") however well the role can read.
 * Every holder of the role can then use those warehouses, and spend their credits. Nothing else about
 * the role changes, and existing mode needs no SYSADMIN. A built-in admin role is refused in both
 * modes. A role that does not exist stops the existing-role script at `SHOW GRANTS OF ROLE`, before
 * the user is created (verified live).
 *
 * Accepted residuals, by construction negligible:
 * - (a) A pre-existing role that happens to carry the generated name is reused and granted access.
 * - (b) A same-named generated user carrying another key passes silently; Test then fails with
 *   390144 (JWT invalid). `IF NOT EXISTS` never replaces a key.
 * - (c) With typed names, re-running after a partial run stops at "already exists" on the admin's
 *   own object. The form's help text says to drop it or type a new name.
 *
 * `DEFAULT_ROLE` does not matter for Lookout: its driver passes the role explicitly on every connect,
 * and the RudderStack mirror carries it too, so a stale default from an earlier wrong-role run is
 * harmless. That is why the script needs no `ALTER USER`, and it has no `ALTER`, `DROP` or
 * `OR REPLACE` anywhere.
 */

/**
 * The unquoted identifiers this script writes: letters, digits and underscores. Snowflake also allows
 * `$`, but the script rejects it (ACT2-1116): `$` starts a session variable and `$$` a string, so a
 * name carrying it reads differently in a worksheet than in the SQL we generated.
 */
const SNOWFLAKE_IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Snowflake identifiers are at most 255 characters. The server enforces the same bound. */
export const SNOWFLAKE_IDENTIFIER_MAX_LENGTH = 255;

/** The error a form shows for a name the setup SQL cannot use. */
export const SNOWFLAKE_IDENTIFIER_MESSAGE = 'Use only letters, numbers and underscores, starting with a letter';

/** The message for a name holding `$`, which Snowflake allows and this script does not. */
export const SNOWFLAKE_DOLLAR_MESSAGE = "Can't contain $. Use only letters, numbers and underscores.";

/** The error for a name the setup SQL cannot use, or null when it can. */
export function snowflakeIdentifierError(name: string): string | null {
  if (isSnowflakeIdentifier(name)) return null;
  return name.includes('$') ? SNOWFLAKE_DOLLAR_MESSAGE : SNOWFLAKE_IDENTIFIER_MESSAGE;
}

/**
 * Snowflake's built-in administrative roles. Existing-role mode hands the service user the role, so
 * Lookout would act with every privilege one of these carries; new-role mode would grant the admin
 * role to the service user too. The form and the generator refuse them in both modes.
 */
export const SNOWFLAKE_ADMIN_ROLES: readonly string[] = [
  'ACCOUNTADMIN',
  'ORGADMIN',
  'GLOBALORGADMIN',
  'SECURITYADMIN',
  'SYSADMIN',
  'USERADMIN',
];

/** The error for an existing role Lookout must not be handed. */
export const SNOWFLAKE_ADMIN_ROLE_MESSAGE = "Can't be a Snowflake admin role. Pick a read-only role.";

/** The error for a new role named after an admin role. */
export const SNOWFLAKE_ADMIN_NEW_ROLE_MESSAGE = "Can't be a Snowflake admin role. Type a new name for Lookout's role.";

/** The error for a role the setup SQL cannot use in this mode (a bad name or an admin role), or null. */
export function snowflakeRoleError(name: string, mode: SnowflakeRoleMode): string | null {
  const error = snowflakeIdentifierError(name);
  if (error !== null) return error;
  if (!SNOWFLAKE_ADMIN_ROLES.includes(name.trim().toUpperCase())) return null;
  return mode === 'existing' ? SNOWFLAKE_ADMIN_ROLE_MESSAGE : SNOWFLAKE_ADMIN_NEW_ROLE_MESSAGE;
}

/** Whether the setup SQL can use this name: the rule `assertIdent` applies, for a form to check field by field. */
export function isSnowflakeIdentifier(name: string): boolean {
  return SNOWFLAKE_IDENT.test(name.trim());
}

/**
 * ACT2-1031: the one grant the Lookout compute warehouse needs, or null when the name is not one the
 * SQL can carry. A missing or unusable role renders as `<ROLE>` for the admin to fill in.
 */
export function lookoutWarehouseGrantSQL(warehouse: string, role: string | null | undefined): string | null {
  if (!isSnowflakeIdentifier(warehouse)) return null;
  const roleName = role && isSnowflakeIdentifier(role) ? role.trim().toUpperCase() : '<ROLE>';
  return `GRANT USAGE ON WAREHOUSE ${warehouse.trim().toUpperCase()} TO ROLE ${roleName};`;
}

/**
 * Every "stops the script" note in this file holds only in a client that stops at the first error.
 * A Snowsight worksheet's Run All does; SnowSQL does not unless told to, and would run on past a
 * failed statement (a taken name, a missing role) into the ones that depended on it.
 */
const STOP_AT_FIRST_ERROR = 'Run All in a Snowsight worksheet; in SnowSQL pass `-o exit_on_error=true`.';

/** Regex for valid Base64 content (letters, digits, +, /, =, whitespace). */
const BASE64 = /^[A-Za-z0-9+/=\s]+$/;

/** Validate and uppercase a Snowflake identifier. Throws a message naming the field on invalid input. */
function assertIdent(name: string, label: string): string {
  const upper = name.trim().toUpperCase();
  const error = snowflakeIdentifierError(upper);
  if (error !== null) throw new Error(`${label}: ${error}`);
  return upper;
}

/**
 * Reverse ETL's internal schema on Snowflake, upper because unquoted identifiers fold up there.
 * The authority is rudder-sources' `GetRudderSchema()`; this generator is frontend-only and pure, so
 * it repeats the literal rather than importing the backend map — the test asserts the two agree.
 */
const RETL_INTERNAL_SCHEMA = '_RUDDERSTACK';

/**
 * Object types that need their own `ON ALL` / `ON FUTURE` grant, with the privileges each accepts.
 *
 * Snowflake evaluates future grants per object type, so every type a customer's warehouse can
 * contain needs a line of its own or it stays invisible. `DYNAMIC TABLES` also covers dynamic
 * Iceberg tables (they are not `ICEBERG TABLES` for grant purposes). Only plain and Iceberg tables
 * accept DML; a dynamic or external table and a materialized view are read-only by construction, so
 * granting them INSERT would fail the statement rather than widen access.
 */
interface ObjectType {
  readonly plural: string;
  readonly writable: boolean;
}

/**
 * Types every Snowflake account has. These grants cannot fail: they run as SECURITYADMIN, which
 * holds MANAGE GRANTS, so no privilege stops them, and no edition lacks a plain table or view.
 */
const CORE_OBJECT_TYPES: readonly ObjectType[] = [
  { plural: 'TABLES', writable: true },
  { plural: 'VIEWS', writable: false },
];

/**
 * Types an account may not have. Materialized views are Enterprise Edition and up; Iceberg and
 * dynamic tables need a recent enough account. A `GRANT … ON FUTURE MATERIALIZED VIEWS` on Standard
 * Edition is rejected outright, and a worksheet stops at the first error — which is why these are
 * emitted LAST, in their own block, behind a note saying everything above already succeeded.
 */
const EXTENDED_OBJECT_TYPES: readonly ObjectType[] = [
  { plural: 'MATERIALIZED VIEWS', writable: false },
  { plural: 'DYNAMIC TABLES', writable: false },
  { plural: 'ICEBERG TABLES', writable: true },
  { plural: 'EXTERNAL TABLES', writable: false },
];

const GRANTABLE_OBJECT_TYPES: readonly ObjectType[] = [...CORE_OBJECT_TYPES, ...EXTENDED_OBJECT_TYPES];

/** Where the data grants apply: the whole database (default) or the one named schema. */
export type GrantScope = 'database' | 'schema';

/**
 * `new`: create a role for Lookout and grant it access. `existing`: use the admin's role, granting it
 * only USAGE on the database and the compute warehouse(s).
 */
export type SnowflakeRoleMode = 'new' | 'existing';

export interface SetupSQLParams {
  username: string;
  publicKeyBody: string;
  /** Named in the verify step, so the admin can match it against `DESC USER`. */
  publicKeyFingerprint?: string;
  warehouse: string;
  /** ACT2-1031: an optional second warehouse for Lookout's own queries, granted alongside `warehouse`. */
  lookoutWarehouse?: string;
  database: string;
  schema?: string;
  /** Required (ACT2-1116): there is no `<user>_ROLE` fallback any more. */
  role: string;
  /** Defaults to `new`. */
  roleMode?: SnowflakeRoleMode;
  /** The user name is the derived default: `CREATE USER IF NOT EXISTS`. Otherwise a plain `CREATE`. */
  generatedUser?: boolean;
  /** The role name is the derived default: `CREATE ROLE IF NOT EXISTS`. Otherwise a plain `CREATE`. */
  generatedRole?: boolean;
  readWrite?: boolean;
  /**
   * Defaults to `database` (ACT2-735): grants cover every current and future schema, so a new schema
   * created after setup is readable without a second visit. `schema` narrows to the named schema for
   * a customer who wants that, and emits the identical object-type set `IN SCHEMA`.
   */
  grantScope?: GrantScope;
}

/** Resolved, validated identifiers — computed once and shared by the script and the grant block. */
interface ResolvedIdents {
  username: string;
  warehouse: string;
  lookoutWarehouse: string | null;
  database: string;
  schema: string;
  role: string;
  qualifiedSchema: string;
  internalSchema: string;
  readWrite: boolean;
  grantScope: GrantScope;
}

function resolveIdents(params: SetupSQLParams): ResolvedIdents {
  const username = assertIdent(params.username, 'Service username');
  const warehouse = assertIdent(params.warehouse, 'Compute warehouse');
  const lookoutWarehouse = params.lookoutWarehouse?.trim()
    ? assertIdent(params.lookoutWarehouse, 'Lookout compute warehouse')
    : null;
  const database = assertIdent(params.database, 'Database');
  const schema = assertIdent(params.schema?.trim() ?? 'PUBLIC', 'Schema');
  const role = assertIdent(params.role, 'Role');
  return {
    username,
    warehouse,
    lookoutWarehouse,
    database,
    schema,
    role,
    qualifiedSchema: `${database}.${schema}`,
    internalSchema: `${database}.${RETL_INTERNAL_SCHEMA}`,
    readWrite: params.readWrite ?? false,
    grantScope: params.grantScope ?? 'database',
  };
}

/** `SELECT` for a read-only account; the full DML set for a read-write one, where the type takes it. */
function privilegesFor(objectType: ObjectType, readWrite: boolean): string {
  return readWrite && objectType.writable ? 'SELECT, INSERT, UPDATE, DELETE' : 'SELECT';
}

/** `ON ALL` + `ON FUTURE` lines for the given object types, at one container. */
function objectTypeGrants(
  container: string,
  containerRef: string,
  role: string,
  readWrite: boolean,
  types: readonly ObjectType[] = GRANTABLE_OBJECT_TYPES,
): string {
  return types
    .flatMap(({ plural, writable }) => {
      const privileges = privilegesFor({ plural, writable }, readWrite);
      return [
        `GRANT ${privileges} ON ALL ${plural} IN ${container} ${containerRef} TO ROLE ${role};`,
        `GRANT ${privileges} ON FUTURE ${plural} IN ${container} ${containerRef} TO ROLE ${role};`,
      ];
    })
    .join('\n');
}

/**
 * The grant block alone, safe to re-run on an existing account and idempotent.
 *
 * This is what an admin needs when the account already exists — provisioned by our older script, or
 * by hand, or left short by a `COPY GRANTS` rotation. The full setup script composes it, so the two
 * can never drift.
 */
export function generateSnowflakeGrantSQL(params: SetupSQLParams): string {
  return grantBlock(resolveIdents(params));
}

function grantBlock(ids: ResolvedIdents): string {
  const { role, database, qualifiedSchema, internalSchema, readWrite, grantScope, warehouse, lookoutWarehouse } = ids;

  // The block switches between SECURITYADMIN and SYSADMIN. `USE ROLE` checks the USER's grants, not
  // the current role's (verified live, ACT2-1116), so a user who holds both can switch freely even
  // though SECURITYADMIN does not inherit SYSADMIN. No ACCOUNTADMIN is needed.
  const header = `-- Rudder Lookout -- grants for role ${role} (${readWrite ? 'read-write' : 'read-only'})
-- Safe to re-run: every statement is idempotent. Use this block on its own to repair an
-- existing account. Run it as a user who holds SECURITYADMIN (to grant) and SYSADMIN (to
-- create the Reverse ETL schema). No warehouse is needed.
-- ⚠ A worksheet stops at the first error. If a statement fails, nothing after it ran.
-- ${STOP_AT_FIRST_ERROR}
USE ROLE SECURITYADMIN;

GRANT USAGE ON WAREHOUSE ${warehouse} TO ROLE ${role};
GRANT USAGE ON DATABASE ${database} TO ROLE ${role};`;

  // Tier 1 — cannot fail. Runs as SECURITYADMIN (MANAGE GRANTS, so no privilege stops it) over
  // object types every account has.
  const coreDataGrants =
    grantScope === 'database'
      ? `
-- Every current and future schema in the database, so a schema created after setup
-- (a new source namespace, a Profiles output, a dbt target) needs no second visit.
GRANT USAGE ON ALL SCHEMAS IN DATABASE ${database} TO ROLE ${role};
GRANT USAGE ON FUTURE SCHEMAS IN DATABASE ${database} TO ROLE ${role};

${objectTypeGrants('DATABASE', database, role, readWrite, CORE_OBJECT_TYPES)}

-- Repeated at schema level for ${qualifiedSchema} on purpose. A schema-level future grant
-- takes precedence over the database-level one for the same object type and silences it --
-- including a grant made to some OTHER role. Emitting both means the schema you named is
-- covered either way. If another schema already carries its own future grants, repeat this
-- block for it too.
GRANT USAGE ON SCHEMA ${qualifiedSchema} TO ROLE ${role};
${objectTypeGrants('SCHEMA', qualifiedSchema, role, readWrite, CORE_OBJECT_TYPES)}`
      : `
-- Single-schema scope. A schema created after setup gets nothing; re-run this block for it,
-- or switch to database scope.
GRANT USAGE ON SCHEMA ${qualifiedSchema} TO ROLE ${role};
${objectTypeGrants('SCHEMA', qualifiedSchema, role, readWrite, CORE_OBJECT_TYPES)}`;

  const writeGrants = readWrite
    ? `
GRANT CREATE TABLE, CREATE VIEW, CREATE TEMPORARY TABLE ON SCHEMA ${qualifiedSchema} TO ROLE ${role};`
    : '';

  // Tier 3 — may be rejected by an account that lacks the feature. Last on purpose: a failure here
  // costs only the exotic object types, and everything the account needs to explore and to activate
  // has already landed.
  const extendedDataGrants =
    grantScope === 'database'
      ? `
-- Everything above this line has already succeeded. The grants below cover object types your
-- account may not have: materialized views need Enterprise Edition, and Iceberg and dynamic
-- tables need a recent enough account. If one is rejected, DELETE that line and re-run just
-- this block -- reads of plain tables and views, and Reverse ETL, are unaffected.
${objectTypeGrants('DATABASE', database, role, readWrite, EXTENDED_OBJECT_TYPES)}

${objectTypeGrants('SCHEMA', qualifiedSchema, role, readWrite, EXTENDED_OBJECT_TYPES)}`
      : `
-- Everything above this line has already succeeded. The grants below cover object types your
-- account may not have (materialized views need Enterprise Edition; Iceberg and dynamic tables
-- need a recent enough account). If one is rejected, DELETE that line and re-run just this block.
${objectTypeGrants('SCHEMA', qualifiedSchema, role, readWrite, EXTENDED_OBJECT_TYPES)}`;

  // Tier 2 — ACT2-737, and the one statement in the whole block that can fail on a PRIVILEGE:
  // SYSADMIN does not own every database. Unconditional and independent of `readWrite`, which is
  // about the customer's own data, while this is Reverse ETL's private bookkeeping schema.
  // Activation needs CREATE and DROP here for its sync_data_*, snapshot_*, unload_*, failed_keys_*,
  // rej_* and sync_log tables, and rudder-sources requires the schema to already EXIST — it never
  // creates it. DML on ALL/FUTURE TABLES matters only for tables some other principal created; rETL
  // owns the ones it makes.
  const internalSchemaGrants = `
-- Reverse ETL's internal schema. Without this an account authenticates, lists tables and
-- saves audiences, then fails at the first activation -- so it is granted even for a
-- read-only account. Nothing customer-facing is written here; these are sync bookkeeping
-- tables (sync_data_*, snapshot_*, failed_keys_*, rej_*, sync_log).
USE ROLE SYSADMIN;
CREATE SCHEMA IF NOT EXISTS ${internalSchema};
-- ⚠ This is the one statement here that can fail on privileges: SYSADMIN does not own every
--   database. Everything above this line has already succeeded, and a worksheet stops at the
--   first error, so nothing below it ran. Fix it with one statement, run as the database's
--   owning role or as ACCOUNTADMIN, then re-run from this line down:
--     GRANT CREATE SCHEMA ON DATABASE ${database} TO ROLE SYSADMIN;
--   (SHOW GRANTS ON DATABASE ${database} lists the owning role under privilege OWNERSHIP. Or
--    create ${internalSchema} by hand as that role and re-run from this line down.)

USE ROLE SECURITYADMIN;
GRANT USAGE, CREATE TABLE, CREATE VIEW ON SCHEMA ${internalSchema} TO ROLE ${role};
GRANT SELECT, INSERT, UPDATE, DELETE ON ALL TABLES IN SCHEMA ${internalSchema} TO ROLE ${role};
GRANT SELECT, INSERT, UPDATE, DELETE ON FUTURE TABLES IN SCHEMA ${internalSchema} TO ROLE ${role};`;

  // Order is load-bearing, and it is ordered by BLAST RADIUS rather than by importance, because a
  // worksheet's Run All stops at the first error and whatever comes after it never runs.
  //
  // Exactly two statements in this block can fail, for unrelated reasons, and an earlier revision
  // put one of them first and the other last — so whichever tripped took the other down with it.
  // They are now separated by everything that cannot fail:
  //
  //   1. grants that cannot fail  — SECURITYADMIN holds MANAGE GRANTS, and every account has plain
  //                                 tables and views
  //   2. CREATE SCHEMA            — can fail on a privilege (SYSADMIN may not own the database)
  //   3. exotic object types      — can be rejected by an account that lacks the feature
  //
  // So a failure at (2) costs only the exotic grants that follow, a failure at (3) costs nothing
  // else at all, and neither can leave the account unable to activate while looking set up.
  // Tier 4 — ACT2-1031, optional. Last for the same reason as tier 3: a warehouse that does not exist
  // yet, or a typo in its name, fails this one statement and nothing the account needs.
  const lookoutWarehouseGrant =
    lookoutWarehouse !== null && lookoutWarehouse !== warehouse
      ? `

-- Lookout's own compute warehouse. Last on purpose: it is optional, so if ${lookoutWarehouse}
-- does not exist yet this line fails and nothing above it is lost. Create the warehouse (or
-- fix its name) and run this line again.
GRANT USAGE ON WAREHOUSE ${lookoutWarehouse} TO ROLE ${role};`
      : '';

  return `${header}
${coreDataGrants}${writeGrants}
${internalSchemaGrants}
${extendedDataGrants}${lookoutWarehouseGrant}`;
}

/** `SHOW` statements that let an admin see what the script actually produced. */
function verificationBlock(ids: ResolvedIdents, fingerprint: string | undefined, roleMode: SnowflakeRoleMode): string {
  const { username, role, database, qualifiedSchema, internalSchema, warehouse } = ids;
  const fpCheck = fingerprint
    ? `-- RSA_PUBLIC_KEY_FP or RSA_PUBLIC_KEY_2_FP should be ${fingerprint}.
-- A different value means this user already existed with another key: Lookout's Test will fail.`
    : '-- Check that RSA_PUBLIC_KEY_FP is set (non-null).';
  const userCheck = `USE ROLE SECURITYADMIN;
DESC USER ${username};
${fpCheck}`;
  if (roleMode === 'existing') {
    return `${userCheck}

SHOW GRANTS TO ROLE ${role};
-- Beyond USAGE on the warehouse and database, Lookout uses ${role} as it is. It needs to read ${database}.

-- Optional: if you hold ${role} yourself, check what it can see, as that role.
-- (USE ROLE fails if you don't hold it; that says nothing about the service user.)
-- USE ROLE ${role};
-- USE WAREHOUSE ${warehouse};
-- SELECT COUNT(*) AS can_see_retl_schema
-- FROM ${database}.INFORMATION_SCHEMA.SCHEMATA
-- WHERE SCHEMA_NAME = '${RETL_INTERNAL_SCHEMA}';`;
  }
  return `${userCheck}

SHOW GRANTS TO ROLE ${role};
SHOW FUTURE GRANTS IN DATABASE ${database};
SHOW FUTURE GRANTS IN SCHEMA ${qualifiedSchema};
-- Every object type above should appear: TABLES, VIEWS, MATERIALIZED VIEWS,
-- DYNAMIC TABLES, ICEBERG TABLES, EXTERNAL TABLES.

SHOW GRANTS ON SCHEMA ${internalSchema};
-- One row per grant above is what keeps activation working.

-- The check that actually matters, run AS THE SERVICE ROLE. An admin role can always see a
-- schema it just created, so verifying as SECURITYADMIN or ACCOUNTADMIN cannot fail even on
-- a broken account -- and role visibility is precisely what was broken. This is the same
-- question Lookout's Test Connection asks, as the same role.
USE ROLE ${role};
-- This check is a query, so unlike the setup above it needs a warehouse (verified live, ACT2-1116).
USE WAREHOUSE ${warehouse};
SELECT COUNT(*) AS can_see_retl_schema
FROM ${database}.INFORMATION_SCHEMA.SCHEMATA
WHERE SCHEMA_NAME = '${RETL_INTERNAL_SCHEMA}';
-- Exactly 1. Zero means this role cannot see the schema, and activation will fail.
-- An exact match, not SHOW SCHEMAS LIKE '${RETL_INTERNAL_SCHEMA}': in a LIKE pattern the
-- leading underscore is a single-character WILDCARD, so that form also matches a schema
-- called XRUDDERSTACK and could report success on the wrong object. Lookout's Test Connection
-- asks the same question of the same role, against the session's own catalog.`;
}

/** `CREATE <kind> [IF NOT EXISTS] <name>`: generated names re-run safely, typed ones stop on a clash. */
function createClause(kind: 'USER' | 'ROLE', name: string, generated: boolean): string {
  return `CREATE ${kind} ${generated ? 'IF NOT EXISTS ' : ''}${name}`;
}

/** The user statement, shared by both role modes. */
function createUserStatement(ids: ResolvedIdents, publicKeyBody: string, generated: boolean): string {
  return `${createClause('USER', ids.username, generated)}
  TYPE = SERVICE
  RSA_PUBLIC_KEY = '${publicKeyBody}'
  DEFAULT_ROLE = ${ids.role}
  DEFAULT_WAREHOUSE = ${ids.warehouse};`;
}

const TYPED_NAME_NOTE = `-- If Snowflake says a name "already exists", stop: choose a different name in Lookout
-- and copy the SQL again.`;

/** Step 1 in `new` mode: user first, then role (see the module doc for why). */
function createNewRoleStep(ids: ResolvedIdents, publicKeyBody: string, generatedUser: boolean, generatedRole: boolean) {
  const { username, role } = ids;
  const note =
    generatedUser && generatedRole
      ? '-- Safe to re-run: these names were generated for this connection.'
      : TYPED_NAME_NOTE;
  return {
    title: 'Create the user and the role',
    body: `${note}
USE ROLE USERADMIN;
${createUserStatement(ids, publicKeyBody, generatedUser)}
${createClause('ROLE', role, generatedRole)};

USE ROLE SECURITYADMIN;
GRANT ROLE ${role} TO ROLE SYSADMIN;
GRANT ROLE ${role} TO USER ${username};`,
  };
}

/**
 * Step 1 in `existing` mode: check the role exists, create the user, give it the role, and grant the
 * role the USAGE a connection needs. Nothing else.
 */
function createExistingRoleStep(ids: ResolvedIdents, publicKeyBody: string, generatedUser: boolean) {
  const { username, role, database, warehouse, lookoutWarehouse } = ids;
  // Last, as in the grant block: an optional warehouse that does not exist yet fails only this line.
  const lookoutWarehouseLine =
    lookoutWarehouse !== null && lookoutWarehouse !== warehouse
      ? `\n-- Lookout's own compute warehouse. Last on purpose: if ${lookoutWarehouse} does not exist yet,
-- only this line fails. Create it (or fix its name) and run this line again.
GRANT USAGE ON WAREHOUSE ${lookoutWarehouse} TO ROLE ${role};`
      : '';
  return {
    title: `Create the user and give it role ${role}`,
    body: `-- Lookout grants role ${role} only the USAGE it needs to connect (below), and changes
-- nothing else about it.${generatedUser ? '' : `\n${TYPED_NAME_NOTE}`}
-- Stops here, before anything is created, if ${role} does not exist:
USE ROLE SECURITYADMIN;
SHOW GRANTS OF ROLE ${role};

USE ROLE USERADMIN;
${createUserStatement(ids, publicKeyBody, generatedUser)}

USE ROLE SECURITYADMIN;
GRANT ROLE ${role} TO USER ${username};

-- Lookout's Test runs a query, which needs a warehouse and the database: without these it fails
-- whatever ${role} can read. Idempotent, so harmless if ${role} already has them.
-- Every user who holds ${role} can then use the warehouse, and spend its credits.
GRANT USAGE ON DATABASE ${database} TO ROLE ${role};
GRANT USAGE ON WAREHOUSE ${warehouse} TO ROLE ${role};${lookoutWarehouseLine}`,
  };
}

/**
 * The admin roles the setup SQL switches to. Only `new` mode creates the Reverse ETL schema, as SYSADMIN;
 * `existing` mode leaves that to the optional activation grants.
 */
export function snowflakeSetupAdminRoles(roleMode: SnowflakeRoleMode): string {
  return roleMode === 'existing' ? 'USERADMIN and SECURITYADMIN' : 'USERADMIN, SECURITYADMIN and SYSADMIN';
}

/**
 * Generate Snowflake setup SQL with:
 * - Unquoted uppercase identifiers (validated at boundary; `$` rejected)
 * - USE ROLE transitions (USERADMIN to create, SECURITYADMIN to grant, SYSADMIN for the rETL schema
 *   in `new` mode)
 * - The user created before the role; `IF NOT EXISTS` only on generated names (ACT2-1116)
 * - In `new` mode: database-scoped ALL + FUTURE grants across every object type, by default, and
 *   grants on Reverse ETL's internal schema, so activation works
 * - In `existing` mode: only USAGE on the warehouse(s) and the database, which a Test needs (see
 *   `generateExistingRoleActivationSQL` for the optional activation grants)
 * - No network policy (ACT2-1031): an IP allowlist on this user also blocks Reverse ETL, which
 *   shares the user and connects from its own region-specific IPs
 * - A verification block
 */
export function generateSetupSQL(params: SetupSQLParams): string {
  const { publicKeyBody } = params;

  // Validate publicKeyBody as Base64 before interpolating into SQL
  if (!BASE64.test(publicKeyBody)) {
    throw new Error('Invalid public key body: expected Base64');
  }

  const ids = resolveIdents(params);
  const { readWrite, grantScope } = ids;
  const roleMode = params.roleMode ?? 'new';
  const generatedUser = params.generatedUser ?? false;
  // The service user gets this role in either mode, so an admin role would give Lookout admin rights.
  const roleError = snowflakeRoleError(ids.role, roleMode);
  if (roleError !== null) throw new Error(`Role: ${roleError}`);

  // Steps are numbered from this list, so adding or skipping one cannot leave the numbering wrong.
  const steps: { title: string; body: string }[] =
    roleMode === 'existing'
      ? [createExistingRoleStep(ids, publicKeyBody, generatedUser)]
      : [
          createNewRoleStep(ids, publicKeyBody, generatedUser, params.generatedRole ?? false),
          {
            title: `Grant access (${readWrite ? 'read-write' : 'read-only'}, ${grantScope} scope)`,
            body: grantBlock(ids),
          },
        ];

  steps.push({
    title: 'Verify (run after setup)',
    body: verificationBlock(ids, params.publicKeyFingerprint, roleMode),
  });

  const body = steps.map((step, index) => `-- Step ${index + 1}: ${step.title}\n${step.body}`).join('\n\n');

  return `-- ============================================================
-- Rudder Lookout -- Snowflake Setup
-- Run as a user who holds ${snowflakeSetupAdminRoles(roleMode)}, in a Snowflake worksheet.
-- No warehouse is needed. USE ROLE checks your own grants, so switching between these works.
-- ${STOP_AT_FIRST_ERROR}
-- ============================================================

${body}
`;
}

/**
 * Existing-role mode only: the grants an activation needs, for the admin to run if their role lacks
 * them. Every line is a comment, so pasting the whole block runs nothing; Lookout never runs it.
 * The schema grants also need `USAGE ON DATABASE` (verified live, ACT2-1116), which the setup step
 * now grants unconditionally, with the warehouse's, so this block carries only the schema.
 */
export function generateExistingRoleActivationSQL(params: Pick<SetupSQLParams, 'role' | 'database'>): string {
  const role = assertIdent(params.role, 'Role');
  const database = assertIdent(params.database, 'Database');
  const internalSchema = `${database}.${RETL_INTERNAL_SCHEMA}`;
  return `-- Optional: grants for activation. Lookout never runs these. Uncomment and run them
-- only if ${role} lacks them.
-- USE ROLE SYSADMIN;
-- CREATE SCHEMA IF NOT EXISTS ${internalSchema};
-- USE ROLE SECURITYADMIN;
-- GRANT USAGE, CREATE TABLE ON SCHEMA ${internalSchema} TO ROLE ${role};
`;
}
